import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  Button, FormControl, FormLabel, Input, Select, Textarea, Checkbox, VStack, HStack,
  useToast, Text, Divider, Box, Badge, Flex, SimpleGrid,
  InputGroup, InputRightElement, InputLeftElement, FormErrorMessage,
} from '@chakra-ui/react';
import { SearchIcon, CheckCircleIcon, InfoIcon } from '@chakra-ui/icons';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY } from '../../../../config/googleMaps';
import tramitesService, { Tramite, TipoTramite } from '../../../../services/tramitesObrasService';

interface ModalTramiteProps {
  isOpen: boolean;
  onClose: () => void;
  tramite?: Tramite | null;
  tiposTramites: TipoTramite[];
  modo: 'new' | 'edit' | 'view';
  onSuccess: () => void;
}

const EMPTY_FORM = {
  idTipoTramite: '',
  fechaSolicitud: new Date().toISOString().split('T')[0],
  fechaSolicitudEsRegistro: false,
  nombreSolicitante: '',
  telefono: '',
  correo: '',
  nomenclatura: '',
  direccion: '',
  observaciones: '',
  origen: 'PRESENCIAL',
};

const ModalTramite: React.FC<ModalTramiteProps> = ({
  isOpen, onClose, tramite, tiposTramites, modo, onSuccess,
}) => {
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [coordenadas, setCoordenadas] = useState({ lat: 19.4326, lng: -99.1332 });
  const [loading, setLoading] = useState(false);
  const [tipoSearch, setTipoSearch] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const toast = useToast();
  const esVisualizacion = modo === 'view';

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: ['places'] as any,
  });

  useEffect(() => {
    if (!isOpen) return;
    setTipoSearch('');
    setSubmitAttempted(false);
    if (tramite) {
      setFormData({
        idTipoTramite: String(tramite.idTipoTramite),
        fechaSolicitud: tramite.fechaSolicitud.split('T')[0],
        fechaSolicitudEsRegistro: tramite.fechaSolicitudEsRegistro,
        nombreSolicitante: tramite.nombreSolicitante,
        telefono: tramite.telefono,
        correo: tramite.correo,
        nomenclatura: tramite.nomenclatura || '',
        direccion: tramite.direccion || '',
        observaciones: tramite.observaciones || '',
        origen: tramite.origen,
      });
      if (tramite.ubicaciones?.[0]) {
        setCoordenadas({ lat: tramite.ubicaciones[0].latitud, lng: tramite.ubicaciones[0].longitud });
      }
    } else {
      setFormData({ ...EMPTY_FORM });
      setCoordenadas({ lat: 19.4326, lng: -99.1332 });
    }
  }, [tramite, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
    if (name === 'fechaSolicitudEsRegistro' && checked) {
      setFormData(prev => ({ ...prev, fechaSolicitud: new Date().toISOString().split('T')[0] }));
    }
  };

  const buscarDireccion = () => {
    if (!formData.direccion) {
      toast({ title: 'Ingrese una dirección para buscar', status: 'warning', duration: 3000 });
      return;
    }
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: formData.direccion }, (results, status) => {
      if (status === 'OK' && results?.[0]) {
        const loc = results[0].geometry.location;
        setCoordenadas({ lat: loc.lat(), lng: loc.lng() });
        setFormData(prev => ({ ...prev, direccion: results[0].formatted_address }));
        toast({ title: 'Dirección encontrada', status: 'success', duration: 2000 });
      } else {
        toast({ title: 'Dirección no encontrada', description: 'Proporciona una dirección más precisa', status: 'error', duration: 3000 });
      }
    });
  };

  // Validation
  const validate = (): Record<string, string> => {
    const e: Record<string, string> = {};
    if (!formData.idTipoTramite) e.idTipoTramite = 'Seleccione un tipo de trámite';
    if (!formData.nombreSolicitante.trim()) e.nombreSolicitante = 'Requerido';
    if (!formData.telefono.trim()) e.telefono = 'Requerido';
    if (!formData.correo.trim()) e.correo = 'Requerido';
    return e;
  };

  const errors = submitAttempted ? validate() : {};

  // Filtered tipo list
  const tiposFiltrados = useMemo(() => {
    if (!tipoSearch.trim()) return tiposTramites;
    const q = tipoSearch.toLowerCase();
    return tiposTramites.filter(t =>
      t.nombre.toLowerCase().includes(q) ||
      (t.codigo?.toLowerCase() || '').includes(q) ||
      (t.descripcion?.toLowerCase() || '').includes(q)
    );
  }, [tiposTramites, tipoSearch]);

  const tipoSeleccionado = tiposTramites.find(t => String(t.id) === formData.idTipoTramite);
  const requisitos = tipoSeleccionado?.requisitos || [];
  const obligatorios = requisitos.filter(r => r.obligatorio);
  const opcionales = requisitos.filter(r => !r.obligatorio);

  const handleSubmit = async () => {
    setSubmitAttempted(true);
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      toast({ title: 'Faltan campos obligatorios', status: 'error', duration: 3000 });
      return;
    }
    setLoading(true);
    try {
      const data: any = {
        idTipoTramite: Number(formData.idTipoTramite),
        fechaSolicitud: formData.fechaSolicitud,
        fechaSolicitudEsRegistro: formData.fechaSolicitudEsRegistro,
        nombreSolicitante: formData.nombreSolicitante,
        telefono: formData.telefono,
        correo: formData.correo,
        nomenclatura: formData.nomenclatura,
        direccion: formData.direccion,
        observaciones: formData.observaciones,
        origen: formData.origen,
      };
      if (coordenadas.lat && coordenadas.lng) {
        data.latitud = coordenadas.lat;
        data.longitud = coordenadas.lng;
        data.urlMaps = `https://www.google.com/maps?q=${coordenadas.lat},${coordenadas.lng}`;
      }
      if (modo === 'edit' && tramite) {
        await tramitesService.actualizarTramite(tramite.id, data);
        toast({ title: 'Trámite actualizado correctamente', status: 'success', duration: 3000 });
      } else {
        await tramitesService.crearTramite(data);
        toast({ title: 'Trámite creado correctamente', status: 'success', duration: 3000 });
      }
      onSuccess();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Error al guardar el trámite',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isLoaded) return <Text p={4}>Cargando mapa...</Text>;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader bg="#5b182b" color="white" borderTopRadius="md">
          {modo === 'view' && tramite ? `Trámite ${tramite.folio}` : modo === 'edit' ? 'Editar Trámite' : 'Nuevo Trámite de Obras Públicas'}
        </ModalHeader>
        <ModalCloseButton color="white" />

        <ModalBody pt={4}>
          <VStack spacing={4} align="stretch">

            {/* View mode summary */}
            {esVisualizacion && tramite && (
              <Box borderRadius="lg" overflow="hidden" borderWidth="1px" borderColor="gray.200" mb={2}>
                <Box bgGradient="linear(to-r, #7b1f35, #5b182b)" color="white" px={4} py={3}>
                  <Flex justify="space-between" align="center" flexWrap="wrap" gap={2}>
                    <Box>
                      <Text fontSize="xs" opacity={0.8} textTransform="uppercase" letterSpacing="0.08em">
                        Expediente de Obras Públicas
                      </Text>
                      <Text fontWeight="bold" fontSize="lg" mt={0.5}>{tramite.folio}</Text>
                      <Text fontSize="sm" opacity={0.9}>{tipoSeleccionado?.nombre || 'Trámite sin clasificación'}</Text>
                    </Box>
                    <Badge
                      colorScheme="blue"
                      fontSize="sm"
                      px={3} py={1}
                      borderRadius="full"
                      bg="whiteAlpha.300"
                      color="white"
                    >
                      {tramite.estatus}
                    </Badge>
                  </Flex>
                </Box>
              </Box>
            )}

            {/* Tipo de trámite: picker en nuevo, select disabled en edición/vista */}
            {modo === 'new' ? (
              <FormControl isRequired isInvalid={!!errors.idTipoTramite}>
                <FormLabel>Tipo de Trámite</FormLabel>
                <InputGroup size="sm" mb={2}>
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Buscar por nombre o código..."
                    value={tipoSearch}
                    onChange={e => setTipoSearch(e.target.value)}
                    bg="gray.50"
                    borderRadius="md"
                  />
                </InputGroup>
                <Box
                  maxH="260px"
                  overflowY="auto"
                  borderWidth="1px"
                  borderColor={errors.idTipoTramite ? 'red.300' : 'gray.200'}
                  borderRadius="lg"
                  bg="white"
                >
                  {tiposFiltrados.length === 0 ? (
                    <Box p={5} textAlign="center">
                      <Text color="gray.400" fontSize="sm">Sin resultados para "{tipoSearch}"</Text>
                    </Box>
                  ) : tiposFiltrados.map((tipo, idx) => {
                    const selected = String(tipo.id) === formData.idTipoTramite;
                    return (
                      <Flex
                        key={tipo.id}
                        px={4} py={3}
                        cursor="pointer"
                        bg={selected ? '#5b182b' : 'white'}
                        color={selected ? 'white' : 'gray.700'}
                        _hover={{ bg: selected ? '#8b1f33' : 'red.50' }}
                        borderBottomWidth={idx < tiposFiltrados.length - 1 ? '1px' : '0'}
                        borderColor="gray.100"
                        align="center"
                        justify="space-between"
                        gap={3}
                        transition="background 0.12s"
                        onClick={() => setFormData(prev => ({ ...prev, idTipoTramite: String(tipo.id) }))}
                      >
                        <Box minW={0} flex={1}>
                          <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>{tipo.nombre}</Text>
                          {tipo.descripcion && (
                            <Text fontSize="xs" opacity={selected ? 0.8 : 0.6} noOfLines={1} mt={0.5}>
                              {tipo.descripcion}
                            </Text>
                          )}
                        </Box>
                        <HStack spacing={1.5} flexShrink={0}>
                          {tipo.requiereUbicacion && (
                            <Badge
                              fontSize="2xs"
                              bg={selected ? 'whiteAlpha.300' : undefined}
                              color={selected ? 'white' : undefined}
                              colorScheme={selected ? undefined : 'blue'}
                              variant={selected ? undefined : 'subtle'}
                            >
                              Ubicación
                            </Badge>
                          )}
                          {(tipoSeleccionado?.requisitos?.length ?? 0) > 0 && selected && (
                            <Badge bg="whiteAlpha.200" color="white" fontSize="2xs">
                              {tipoSeleccionado!.requisitos!.length} req.
                            </Badge>
                          )}
                        </HStack>
                      </Flex>
                    );
                  })}
                </Box>
                <FormErrorMessage>{errors.idTipoTramite}</FormErrorMessage>
              </FormControl>
            ) : (
              <FormControl isRequired>
                <FormLabel>Tipo de Trámite</FormLabel>
                <Select
                  name="idTipoTramite"
                  value={formData.idTipoTramite}
                  onChange={handleChange}
                  isDisabled
                >
                  <option value="">Seleccione un tipo</option>
                  {tiposTramites.map(tipo => (
                    <option key={tipo.id} value={tipo.id}>{tipo.nombre}</option>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Tipo seleccionado: info + requisitos */}
            {tipoSeleccionado && (
              <Box borderRadius="lg" overflow="hidden" borderWidth="1px" borderColor="red.100">
                <Box bg="#5b182b" px={4} py={2.5} color="white">
                  <Flex justify="space-between" align="center" gap={2} flexWrap="wrap">
                    <Text fontWeight="bold" fontSize="sm">{tipoSeleccionado.nombre}</Text>
                    <HStack spacing={1.5}>
                      {tipoSeleccionado.requiereUbicacion && (
                        <Badge bg="whiteAlpha.300" color="white" fontSize="2xs">Requiere ubicación</Badge>
                      )}
                      {tipoSeleccionado.requiereDireccion && (
                        <Badge bg="whiteAlpha.300" color="white" fontSize="2xs">Requiere dirección</Badge>
                      )}
                    </HStack>
                  </Flex>
                  {tipoSeleccionado.descripcion && (
                    <Text fontSize="xs" opacity={0.8} mt={0.5}>{tipoSeleccionado.descripcion}</Text>
                  )}
                </Box>
                {requisitos.length > 0 && (
                  <Box px={4} py={3} bg="red.50">
                    <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={2} textTransform="uppercase" letterSpacing="0.06em">
                      Requisitos del trámite
                    </Text>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={1.5}>
                      {[...obligatorios, ...opcionales]
                        .sort((a, b) => a.orden - b.orden)
                        .map(req => (
                          <HStack key={req.id} align="start" spacing={2} py={0.5}>
                            <Box mt="2px" flexShrink={0}>
                              {req.obligatorio
                                ? <CheckCircleIcon color="green.500" boxSize={3.5} />
                                : <InfoIcon color="gray.400" boxSize={3.5} />
                              }
                            </Box>
                            <Box>
                              <Text
                                fontSize="xs"
                                fontWeight={req.obligatorio ? 'semibold' : 'normal'}
                                color={req.obligatorio ? 'gray.800' : 'gray.500'}
                                lineHeight="1.4"
                              >
                                {req.nombreRequisito}
                                {req.obligatorio && <Text as="span" color="red.500"> *</Text>}
                              </Text>
                              {req.descripcion && (
                                <Text fontSize="2xs" color="gray.400">{req.descripcion}</Text>
                              )}
                            </Box>
                          </HStack>
                        ))}
                    </SimpleGrid>
                    <Text fontSize="2xs" color="gray.400" mt={2}>
                      * Obligatorio · {obligatorios.length} obligatorio{obligatorios.length !== 1 ? 's' : ''} · {opcionales.length} opcional{opcionales.length !== 1 ? 'es' : ''}
                    </Text>
                  </Box>
                )}
              </Box>
            )}

            <Divider />

            {/* Fecha */}
            <HStack align="end">
              <FormControl isRequired flex={1}>
                <FormLabel>Fecha de Solicitud</FormLabel>
                <Input
                  type="date"
                  name="fechaSolicitud"
                  value={formData.fechaSolicitud}
                  onChange={handleChange}
                  isDisabled={formData.fechaSolicitudEsRegistro || esVisualizacion}
                />
              </FormControl>
              <FormControl flex={1} pb={2}>
                <Checkbox
                  name="fechaSolicitudEsRegistro"
                  isChecked={formData.fechaSolicitudEsRegistro}
                  onChange={handleCheckboxChange}
                  isDisabled={esVisualizacion}
                  colorScheme="red"
                >
                  <Text fontSize="sm">Igual a fecha de registro</Text>
                </Checkbox>
              </FormControl>
            </HStack>

            {/* Solicitante */}
            <FormControl isRequired isInvalid={!!errors.nombreSolicitante}>
              <FormLabel>Nombre del Solicitante</FormLabel>
              <Input
                name="nombreSolicitante"
                value={formData.nombreSolicitante}
                onChange={handleChange}
                isReadOnly={esVisualizacion}
                placeholder="Nombre completo"
              />
              <FormErrorMessage>{errors.nombreSolicitante}</FormErrorMessage>
            </FormControl>

            <HStack>
              <FormControl isRequired isInvalid={!!errors.telefono}>
                <FormLabel>Teléfono</FormLabel>
                <Input
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                  isReadOnly={esVisualizacion}
                  placeholder="10 dígitos"
                />
                <FormErrorMessage>{errors.telefono}</FormErrorMessage>
              </FormControl>
              <FormControl isRequired isInvalid={!!errors.correo}>
                <FormLabel>Correo</FormLabel>
                <Input
                  type="email"
                  name="correo"
                  value={formData.correo}
                  onChange={handleChange}
                  isReadOnly={esVisualizacion}
                />
                <FormErrorMessage>{errors.correo}</FormErrorMessage>
              </FormControl>
            </HStack>

            <FormControl>
              <FormLabel>Nomenclatura</FormLabel>
              <Textarea
                name="nomenclatura"
                value={formData.nomenclatura}
                onChange={handleChange}
                rows={2}
                isReadOnly={esVisualizacion}
                placeholder="Lote, manzana, fraccionamiento, clave catastral, etc."
              />
            </FormControl>

            {/* Dirección + mapa */}
            <FormControl>
              <FormLabel>Dirección</FormLabel>
              <InputGroup>
                <Input
                  name="direccion"
                  value={formData.direccion}
                  onChange={handleChange}
                  placeholder="Calle, número, colonia, municipio"
                  isReadOnly={esVisualizacion}
                />
                {!esVisualizacion && (
                  <InputRightElement width="4.5rem">
                    <Button h="1.75rem" size="sm" colorScheme="red" variant="ghost" onClick={buscarDireccion}>
                      Buscar
                    </Button>
                  </InputRightElement>
                )}
              </InputGroup>
            </FormControl>

            <FormControl>
              <FormLabel>Ubicación en Google Maps</FormLabel>
              <Box h="220px" w="100%" borderRadius="md" overflow="hidden" borderWidth="1px" borderColor="gray.200">
                <GoogleMap
                  zoom={15}
                  center={coordenadas}
                  mapContainerStyle={{ width: '100%', height: '100%' }}
                >
                  <Marker
                    position={coordenadas}
                    draggable={!esVisualizacion}
                    onDragEnd={e => {
                      if (e.latLng && !esVisualizacion) {
                        setCoordenadas({ lat: e.latLng.lat(), lng: e.latLng.lng() });
                      }
                    }}
                  />
                </GoogleMap>
              </Box>
              {!esVisualizacion && (
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Arrastra el marcador para ajustar la ubicación exacta
                </Text>
              )}
            </FormControl>

            <FormControl>
              <FormLabel>Origen</FormLabel>
              <Select
                name="origen"
                value={formData.origen}
                onChange={handleChange}
                isDisabled={esVisualizacion}
              >
                <option value="PRESENCIAL">Presencial</option>
                <option value="ONLINE">En línea</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Observaciones</FormLabel>
              <Textarea
                name="observaciones"
                value={formData.observaciones}
                onChange={handleChange}
                rows={3}
                isReadOnly={esVisualizacion}
                placeholder="Observaciones generales del trámite"
              />
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px solid #eee">
          <Button variant="ghost" mr={3} onClick={onClose}>
            {esVisualizacion ? 'Cerrar' : 'Cancelar'}
          </Button>
          {!esVisualizacion && (
            <Button bg="#5b182b" color="white" _hover={{ bg: '#8b1f33' }} onClick={handleSubmit} isLoading={loading}>
              {modo === 'edit' ? 'Guardar Cambios' : 'Crear Trámite'}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalTramite;
